/**
 * SettingsTab - Advanced settings and plan management
 *
 * Contains:
 * - Advanced training constants
 * - Max session duration
 * - Plan management (skip ahead)
 * - Fatigue level
 * - Debug mode toggle
 * - Restart onboarding
 */

import {
  BugReport as BugReportIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  RestartAlt as RestartIcon,
  FastForward as SkipIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { FatigueLevel, RunnerCategory, TrainingConstants, UserProfile } from '@shared/types';
import React, { useState } from 'react';
import { useDebug } from '../../../../contexts/DebugContext';
import apiClient from '../../../../services/api';
import { logger } from '../../../../utils/logger';

interface SettingsTabProps {
  profile: UserProfile;
  onUpdate: (updates: Partial<UserProfile>, requiresConfirmation?: boolean) => void;
  pendingChanges: Set<string>;
  onClose: () => void;
}

const FATIGUE_LEVELS: { value: FatigueLevel; label: string; description: string }[] = [
  { value: 'low', label: 'Low', description: 'Feeling fresh and energetic' },
  { value: 'normal', label: 'Normal', description: 'Regular training readiness' },
  { value: 'high', label: 'High', description: 'Feeling tired, need easier workouts' },
];

const deriveRunnerCategoryFromWeeklyMileage = (weeklyMileage: number): RunnerCategory => {
  if (weeklyMileage < 20) return 'beginner';
  if (weeklyMileage <= 70) return 'intermediate';
  return 'advanced';
};

const getLongRunPercentageForCategory = (category: RunnerCategory): number => {
  switch (category) {
    case 'beginner':
      return 0.45;
    case 'intermediate':
      return 0.3;
    case 'advanced':
    default:
      return 0.25;
  }
};

const DEFAULT_TRAINING_CONSTANTS: TrainingConstants = {
  progressiveIncrease: 0.08,
  deloadPercentage: 0.7,
  recoveryPercentage: 0.95,
  longRunPercentage: 0.25,
  baseVolumePercentage: 0.8,
  weeksToGenerate: 5,
};

interface SettingSliderProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
  tooltip: string;
  formatLabel?: (value: number) => string;
}

const SettingSlider: React.FC<SettingSliderProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  step,
  tooltip,
  formatLabel = v => v?.toString() ?? '0',
}) => (
  <Box sx={{ mb: 2 }}>
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
      <Box display="flex" alignItems="center">
        <Typography variant="body2" fontWeight="medium">
          {label}
        </Typography>
        <Tooltip title={tooltip} arrow placement="top">
          <IconButton size="small" sx={{ ml: 0.5, p: 0.25 }}>
            <InfoIcon sx={{ fontSize: 14, opacity: 0.6 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="body2" color="primary.main" fontWeight="bold">
        {formatLabel(value ?? 0)}
      </Typography>
    </Box>
    <Slider
      value={value ?? 0}
      min={min}
      max={max}
      step={step}
      onChange={(_, newValue) => onChange(newValue as number)}
      size="small"
      valueLabelDisplay="auto"
      valueLabelFormat={v => formatLabel(v ?? 0)}
    />
  </Box>
);

const SettingsTab: React.FC<SettingsTabProps> = ({ profile, onUpdate, onClose }) => {
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const [weeksToSkip, setWeeksToSkip] = useState(1);
  const [isSkipping, setIsSkipping] = useState(false);

  const { isDebugMode, setDebugMode, logs, clearLogs } = useDebug();

  const trainingConstants = profile.trainingConstants || DEFAULT_TRAINING_CONSTANTS;
  const category =
    profile.runnerCategory ?? deriveRunnerCategoryFromWeeklyMileage(profile.weeklyMileage);

  const handleTrainingConstantChange = (field: keyof TrainingConstants, value: number) => {
    const newConstants = { ...trainingConstants, [field]: value };
    onUpdate({ trainingConstants: newConstants }, false);
  };

  const handleFatigueLevelChange = (level: FatigueLevel) => {
    // Fatigue level auto-saves (no plan recalculation needed)
    onUpdate({ fatigueLevel: level }, false);
  };

  const handleMaxSessionChange = (value: number) => {
    onUpdate({ maxSessionDurationMin: Math.max(30, Math.min(480, value)) }, false);
  };

  const handleResetDefaults = () => {
    const defaults: TrainingConstants = {
      ...DEFAULT_TRAINING_CONSTANTS,
      longRunPercentage: getLongRunPercentageForCategory(category),
    };
    onUpdate({ trainingConstants: defaults }, false);
  };

  const handleSkipAhead = async () => {
    setShowSkipConfirm(false);
    setIsSkipping(true);

    try {
      const response = await apiClient.skipAhead(weeksToSkip);
      if (response.success && response.data) {
        await apiClient.updateStoredTrainingPlan(response.data);
        window.dispatchEvent(new CustomEvent('training-plan-updated', { detail: response.data }));
        onClose();
      }
    } catch (error) {
      logger.error('Failed to skip ahead', error as Error);
    } finally {
      setIsSkipping(false);
    }
  };

  const handleRestartOnboarding = () => {
    setShowRestartConfirm(false);
    localStorage.removeItem('pacepilot_onboarding_complete');
    localStorage.removeItem('pacepilot_user_profile');
    window.location.href = '/onboarding';
  };

  const formatPercentage = (value: number): string => `${Math.round(value * 100)}%`;

  return (
    <Box>
      {/* Section Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          ⚙️ Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Fine-tune your training plan parameters.
        </Typography>
      </Box>

      {/* Fatigue Level */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            Current Fatigue Level
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {FATIGUE_LEVELS.map(level => (
              <Button
                key={level.value}
                variant={profile.fatigueLevel === level.value ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handleFatigueLevelChange(level.value)}
                sx={{
                  flex: 1,
                  minWidth: 80,
                  flexDirection: 'column',
                  py: 1,
                }}
                aria-label={`Set fatigue level to ${level.label}`}
              >
                <Typography variant="body2" fontWeight={500}>
                  {level.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.8,
                    fontSize: '0.6rem',
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  {level.description}
                </Typography>
              </Button>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Max Session Duration */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Max Long Workout Duration
        </Typography>
        <TextField
          fullWidth
          type="number"
          value={profile.maxSessionDurationMin ?? 120}
          onChange={e => handleMaxSessionChange(parseInt(e.target.value, 10) || 60)}
          helperText="Maximum duration for long runs or rides"
          size="small"
          aria-label="Maximum session duration in minutes"
          InputProps={{
            endAdornment: (
              <Typography variant="caption" color="text.secondary">
                min
              </Typography>
            ),
          }}
          inputProps={{ min: 30, max: 480, step: 15 }}
        />
      </Box>

      {/* Training Constants */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="subtitle2" fontWeight={600}>
              Training Constants
            </Typography>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleResetDefaults}
              sx={{ fontSize: '0.75rem' }}
              aria-label="Reset training constants to defaults"
            >
              Reset
            </Button>
          </Box>

          <SettingSlider
            label="Weekly Increase"
            value={trainingConstants.progressiveIncrease ?? 0.08}
            onChange={v => handleTrainingConstantChange('progressiveIncrease', v)}
            min={0}
            max={0.15}
            step={0.01}
            tooltip="How much to increase weekly volume each week"
            formatLabel={formatPercentage}
          />

          <SettingSlider
            label="Deload Reduction"
            value={trainingConstants.deloadPercentage ?? 0.7}
            onChange={v => handleTrainingConstantChange('deloadPercentage', v)}
            min={0.5}
            max={0.9}
            step={0.05}
            tooltip="Volume reduction during deload weeks"
            formatLabel={formatPercentage}
          />

          <SettingSlider
            label="Long Run Percentage"
            value={trainingConstants.longRunPercentage ?? 0.25}
            onChange={v => handleTrainingConstantChange('longRunPercentage', v)}
            min={0.15}
            max={0.45}
            step={0.05}
            tooltip="Percentage of weekly volume for long run"
            formatLabel={formatPercentage}
          />

          <SettingSlider
            label="Weeks to Generate"
            value={trainingConstants.weeksToGenerate ?? 5}
            onChange={v => handleTrainingConstantChange('weeksToGenerate', v)}
            min={4}
            max={16}
            step={1}
            tooltip="How many weeks ahead to generate"
            formatLabel={v => `${v} weeks`}
          />
        </CardContent>
      </Card>

      {/* Plan Management */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
            Plan Management
          </Typography>

          <Box display="flex" gap={2} alignItems="flex-end">
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Skip</InputLabel>
              <Select
                value={weeksToSkip}
                label="Skip"
                onChange={e => setWeeksToSkip(Number(e.target.value))}
                aria-label="Select weeks to skip"
              >
                {[1, 2, 3, 4].map(n => (
                  <MenuItem key={n} value={n}>
                    {n} week{n > 1 ? 's' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              onClick={() => setShowSkipConfirm(true)}
              disabled={isSkipping}
              startIcon={isSkipping ? <CircularProgress size={16} /> : <SkipIcon />}
              aria-label="Skip ahead in training plan"
            >
              Skip Ahead
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Skips forward in your plan, removing the skipped weeks.
          </Typography>
        </CardContent>
      </Card>

      {/* Debug Mode */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <BugReportIcon fontSize="small" color={isDebugMode ? 'warning' : 'action'} />
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  Debug Mode
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Show on-screen logs for troubleshooting
                </Typography>
              </Box>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={isDebugMode}
                  onChange={e => setDebugMode(e.target.checked)}
                  color="warning"
                />
              }
              label=""
              aria-label="Toggle debug mode"
            />
          </Box>
          {isDebugMode && logs.length > 0 && (
            <Box mt={1}>
              <Button
                variant="text"
                size="small"
                color="secondary"
                onClick={clearLogs}
                aria-label="Clear debug logs"
              >
                Clear {logs.length} Logs
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Restart Onboarding */}
      <Card variant="outlined" sx={{ borderColor: 'error.light' }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                Restart Onboarding
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Go through the setup wizard again
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => setShowRestartConfirm(true)}
              startIcon={<RestartIcon />}
              aria-label="Restart onboarding wizard"
            >
              Restart
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Skip Confirmation Dialog */}
      <Dialog open={showSkipConfirm} onClose={() => setShowSkipConfirm(false)}>
        <DialogTitle>
          Skip {weeksToSkip} Week{weeksToSkip > 1 ? 's' : ''}?
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone.
          </Alert>
          <Typography variant="body2">
            The next {weeksToSkip} week{weeksToSkip > 1 ? 's' : ''} of workouts will be removed and
            future workouts will be shifted forward.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSkipConfirm(false)} aria-label="Cancel skip">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSkipAhead} aria-label="Confirm skip ahead">
            Skip
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restart Confirmation Dialog */}
      <Dialog open={showRestartConfirm} onClose={() => setShowRestartConfirm(false)}>
        <DialogTitle>Restart Onboarding?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            This will take you back to the onboarding wizard.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your cached profile will be cleared locally. Completing the wizard will save your new
            settings.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRestartConfirm(false)} aria-label="Cancel restart">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRestartOnboarding}
            aria-label="Confirm restart onboarding"
          >
            Restart
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsTab;
